import { useMemo, useCallback } from 'react';
import { useAuthStore } from '../stores/auth.store';
import type { UserRole } from '../types/auth.types';

/**
 * Permission map — defines which roles can access each route/feature
 */
const ROUTE_PERMISSIONS: Record<string, UserRole[]> = {
  '/dashboard': ['admin', 'manager', 'sales', 'viewer'],
  '/pipeline': ['admin', 'manager', 'sales', 'viewer'],
  '/contacts': ['admin', 'manager', 'sales', 'viewer'],
  '/whatsapp': ['admin', 'manager', 'sales'],
  '/automations': ['admin', 'manager'],
  '/fluxos': ['admin', 'manager'],
  // Campanhas de WhatsApp — é esta entrada que esconde o item do menu de
  // SALES/VIEWER (o Sidebar filtra por aqui, não pelo `roles` do NavItem).
  '/campanhas': ['admin', 'manager'],
  '/reports': ['admin', 'manager'],
  '/settings': ['admin', 'manager', 'sales'],
  // Any CRM role can REACH /billing — the page gates on `isBillingOwner`
  // (a non-admin can be the billing owner). Keep this in sync with the
  // `/billing` route in App.tsx; restricting it caused a redirect loop for a
  // manager-owner during the paywall.
  '/billing': ['admin', 'manager', 'sales', 'viewer'],
};

/**
 * Feature-level permissions
 */
type Feature =
  | 'contacts:create'
  | 'contacts:edit'
  | 'contacts:delete'
  | 'pipeline:move'
  | 'pipeline:create'
  | 'pipeline:delete'
  | 'tasks:create'
  | 'automations:create'
  | 'automations:edit'
  | 'automations:delete'
  | 'settings:team'
  | 'settings:integrations'
  | 'settings:profile'
  | 'settings:notifications';

const FEATURE_PERMISSIONS: Record<Feature, UserRole[]> = {
  'contacts:create': ['admin', 'manager', 'sales'],
  'contacts:edit': ['admin', 'manager', 'sales'],
  'contacts:delete': ['admin', 'manager'],
  'pipeline:move': ['admin', 'manager', 'sales'],
  'pipeline:create': ['admin', 'manager'],
  'pipeline:delete': ['admin'],
  'tasks:create': ['admin', 'manager', 'sales'],
  'automations:create': ['admin', 'manager'],
  'automations:edit': ['admin', 'manager'],
  'automations:delete': ['admin'],
  'settings:team': ['admin', 'manager'],
  'settings:integrations': ['admin'],
  'settings:profile': ['admin', 'manager', 'sales', 'viewer'],
  'settings:notifications': ['admin', 'manager', 'sales', 'viewer'],
};

/**
 * Role hierarchy — higher level means more permissions
 */
const ROLE_LEVEL: Record<UserRole, number> = {
  viewer: 1,
  sales: 2,
  manager: 3,
  admin: 4,
  org_owner: 5,
  master: 6, // Highest level - system-wide access
};

export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? 'viewer';

  const hasRole = useCallback(
    (requiredRoles: UserRole[]): boolean => {
      return requiredRoles.includes(role);
    },
    [role],
  );

  const hasMinRole = useCallback(
    (minRole: UserRole): boolean => {
      return ROLE_LEVEL[role] >= ROLE_LEVEL[minRole];
    },
    [role],
  );

  const canAccessRoute = useCallback(
    (path: string): boolean => {
      // Master users can only access /system routes
      if (role === 'master') {
        return path.startsWith('/system');
      }
      // org_owner users can only access /system routes (management role)
      if (role === 'org_owner') {
        return path.startsWith('/system');
      }
      const allowedRoles = ROUTE_PERMISSIONS[path];
      if (!allowedRoles) return true; // Routes not in map are open to all authenticated users
      return allowedRoles.includes(role);
    },
    [role],
  );

  const canPerform = useCallback(
    (feature: Feature): boolean => {
      const allowedRoles = FEATURE_PERMISSIONS[feature];
      if (!allowedRoles) return false;
      return allowedRoles.includes(role);
    },
    [role],
  );

  const isReadOnly = useMemo(() => role === 'viewer', [role]);

  const isAdmin = useMemo(() => role === 'admin', [role]);
  const isManager = useMemo(() => role === 'manager', [role]);
  const isSales = useMemo(() => role === 'sales', [role]);
  const isMaster = useMemo(() => role === 'master', [role]);
  const isOrgOwner = useMemo(() => role === 'org_owner', [role]);

  return {
    role,
    hasRole,
    hasMinRole,
    canAccessRoute,
    canPerform,
    isReadOnly,
    isAdmin,
    isManager,
    isSales,
    isMaster,
    isOrgOwner,
  };
}

export { ROUTE_PERMISSIONS, FEATURE_PERMISSIONS, ROLE_LEVEL };
export type { Feature };
